import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/authContext.ts";
import {
  apiConfigurada,
  listarCasos,
  resolverCaso,
  type CasoRow,
} from "../../lib/api.ts";
import { C, st, fecha } from "../../ui/theme.ts";
import { Chip } from "../../ui/componentes.tsx";
import { IconoCumplimiento } from "../../ui/iconos.tsx";

/**
 * CUMPLIMIENTO — casos que exigen decisión FIRMADA del REC (0024). Los casos
 * nacen del motor (efectivo prohibido, PEP); aquí se deciden con fundamento y
 * la decisión queda sellada en bitácora. Nada se resuelve en silencio.
 */

const OPACIDAD_DESHABILITADO = 0.6;

const TIPO_ETIQUETA: Record<string, string> = {
  efectivo_prohibido: "Efectivo prohibido (Art. 32)",
  pep_aprobacion: "Aprobación de PEP (dirección)",
  bc_compartido: "Beneficiario controlador compartido",
  otro: "Otro",
};

export function CumplimientoView() {
  const { session } = useAuth();
  const token = session?.accessToken ?? "";
  const demo = !apiConfigurada() || token === "";

  const [casos, setCasos] = useState<CasoRow[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(!demo);
  const [decision, setDecision] = useState("");
  const [fundamento, setFundamento] = useState("");
  const [guardando, setGuardando] = useState(false);

  const sel = casos.find((c) => c.id === selId) ?? null;

  const recargar = useCallback(async () => {
    if (demo) return;
    setCasos(await listarCasos(token));
  }, [demo, token]);

  useEffect(() => {
    if (demo) return;
    setCargando(true);
    recargar()
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudieron cargar los casos."); })
      .finally(() => { setCargando(false); });
  }, [demo, recargar]);

  const abiertos = casos.filter((c) => c.estado === "abierto");
  const resueltos = casos.filter((c) => c.estado === "resuelto");

  return (
    <div className="argus-master-detail" style={{ display: "flex", flex: 1, minWidth: 0 }}>
      {/* Lista central */}
      <section className="argus-list-pane" style={{ width: 330, flexShrink: 0, borderRight: `1px solid ${C.line}`, display: "flex", flexDirection: "column", background: "#FBFCFB" }}>
        <div style={{ padding: "16px 16px 10px" }}>
          <h2 style={{ ...st.display, margin: 0, fontSize: 18 }}>Cumplimiento</h2>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>
            Casos que exigen tu decisión firmada. Nacen del motor; nada se resuelve en silencio.
          </p>
        </div>
        {demo && (
          <div style={{ margin: "8px 16px", padding: "6px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 11.5, fontWeight: 600 }}>
            Modo demostración — los casos reales nacen del motor con el backend conectado.
          </div>
        )}
        <div style={{ flex: 1, overflow: "auto", padding: "6px 12px 8px" }}>
          {cargando && <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>Cargando…</p>}
          {!cargando && casos.length === 0 && (
            <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>
              Sin casos — el motor los abre solo cuando algo exige tu decisión.
            </p>
          )}
          {[...abiertos, ...resueltos].map((c) => {
            const activa = selId === c.id;
            return (
              <button
                key={c.id}
                onClick={() => { setSelId(c.id); setError(null); setDecision(""); setFundamento(""); }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "#fff",
                  border: `1px solid ${activa ? C.tiel : C.line}`,
                  borderLeft: `4px solid ${c.estado === "abierto" ? C.red : C.green}`,
                  boxShadow: activa ? `0 0 0 1px ${C.tiel}` : "none",
                  borderRadius: 12,
                  padding: "10px 12px",
                  marginBottom: 8,
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13.5, color: C.ink }}>{c.titulo}</div>
                <div style={{ fontSize: 12, color: C.muted, margin: "2px 0 6px" }}>
                  {TIPO_ETIQUETA[c.tipo] ?? c.tipo}
                </div>
                <Chip tono={c.estado === "abierto" ? "rojo" : "verde"}>
                  {c.estado === "abierto" ? "Requiere decisión" : "Resuelto"}
                </Chip>
              </button>
            );
          })}
        </div>
      </section>

      {/* Detalle */}
      <main className="argus-detail-pane" style={{ flex: 1, overflow: "auto", padding: "22px 26px", minWidth: 0 }}>
        {error && (
          <div style={{ maxWidth: 560, margin: "0 auto 12px", padding: "10px 14px", borderRadius: 10, background: C.redBg, color: C.red, fontSize: 13 }}>
            {error}
          </div>
        )}
        {sel ? (
          <div style={{ maxWidth: 560, margin: "0 auto" }}>
            <h2 style={{ margin: "0 0 2px", fontSize: 20 }}>{sel.titulo}</h2>
            <p style={{ margin: "0 0 14px", color: C.muted, fontSize: 13 }}>
              {TIPO_ETIQUETA[sel.tipo] ?? sel.tipo} · abierto {fecha(sel.created_at)}
            </p>

            <div style={st.tarjeta}>
              <p style={st.eyebrow}>El caso</p>
              <div style={{ fontSize: 13.5, color: C.ink, lineHeight: 1.6 }}>
                {sel.descripcion ?? "Sin descripción."}
              </div>
            </div>

            {sel.estado === "abierto" ? (
              <div style={st.tarjeta}>
                <p style={st.eyebrow}>Tu decisión (queda sellada en bitácora)</p>
                <div style={{ display: "grid", gap: 10 }}>
                  <div>
                    <label style={st.label}>Decisión</label>
                    <input
                      style={st.input}
                      placeholder="Ej. Rechazar el excedente en efectivo / Aprobar relación e iniciar DDR"
                      value={decision}
                      onChange={(e) => { setDecision(e.target.value); }}
                    />
                  </div>
                  <div>
                    <label style={st.label}>Fundamento (obligatorio)</label>
                    <textarea
                      style={{ ...st.input, minHeight: 72 }}
                      placeholder="Por qué decides esto — es tu constancia ante una verificación."
                      value={fundamento}
                      onChange={(e) => { setFundamento(e.target.value); }}
                    />
                  </div>
                  <button
                    style={{ ...st.btnPrim, opacity: guardando || decision.trim() === "" || fundamento.trim() === "" ? OPACIDAD_DESHABILITADO : 1 }}
                    disabled={guardando || decision.trim() === "" || fundamento.trim() === ""}
                    onClick={() => {
                      setGuardando(true);
                      setError(null);
                      const run = async () => {
                        if (demo) {
                          setCasos((xs) => xs.map((c) => (c.id === sel.id ? { ...c, estado: "resuelto", decision: decision.trim(), fundamento: fundamento.trim(), decidido_en: new Date().toISOString() } : c)));
                        } else {
                          await resolverCaso(token, sel.id, decision.trim(), fundamento.trim());
                          await recargar();
                        }
                      };
                      void run()
                        .catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudo resolver el caso."); })
                        .finally(() => { setGuardando(false); });
                    }}
                  >
                    {guardando ? "Firmando…" : "Firmar decisión"}
                  </button>
                  <p style={{ margin: 0, fontSize: 12, color: C.muted }}>
                    Sin fundamento no se puede firmar. La decisión y su porqué se conservan 10 años.
                  </p>
                </div>
              </div>
            ) : (
              <div style={{ ...st.tarjeta, background: C.greenBg, borderColor: "#BFE3CC" }}>
                <p style={{ ...st.eyebrow, color: C.green }}>Decisión firmada</p>
                <div style={{ fontSize: 13.5, color: C.ink }}>
                  <b>{sel.decision}</b>
                  <div style={{ marginTop: 6, color: C.muted, fontSize: 12.5 }}>{sel.fundamento}</div>
                  <div style={{ marginTop: 6, fontSize: 12, color: C.muted }}>
                    Sellada el {fecha(sel.decidido_en)} · en bitácora, se conserva 10 años.
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ maxWidth: 420, margin: "18vh auto 0", textAlign: "center", color: C.muted }}>
            <div style={{ marginBottom: 8, color: C.tielDark }}><IconoCumplimiento size={34} /></div>
            <h3 style={{ margin: "0 0 6px", color: C.ink }}>Selecciona un caso</h3>
            <p style={{ fontSize: 13.5, margin: 0 }}>
              Cada caso exige una decisión tuya, con fundamento, sellada en bitácora.
              El motor los abre solo: efectivo prohibido, relaciones con PEP.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
